import { create } from 'zustand';
import {
  getDB,
  ConflictEntry,
  SyncMutation,
  PendingUpload,
  isCacheUnavailableError,
  onOfflineCacheQuarantineChange,
} from './db';

/**
 * NOTE ON WHAT THIS FILE MAY IMPORT
 *
 * The store is imported by leaf components (`PhotoUploader`, `SyncStatusBar`,
 * field screens). It must stay at the bottom of the dependency graph: pulling
 * `sync-queue` in here would drag `uuid` and `apiClient` into every one of
 * them. The reads that populate these counters therefore live in
 * `sync-counts.ts`, which imports the queues *and* this store, and is only
 * pulled in by the handful of modules that actually drive a sync.
 */

interface SyncState {
  isOnline: boolean;
  isSyncing: boolean;
  lastSyncAt: string | null;
  pendingMutations: number;
  pendingUploads: number;
  /**
   * Work that has exhausted its retries.
   *
   * Held separately from `pendingMutations`/`pendingUploads` on purpose. The
   * pending counts answer "will this sync on its own?"; these answer "did
   * something not save?". Folding them together would put failed work behind
   * an orange "waiting to sync" bar that is telling the user a falsehood —
   * nothing is waiting, it has stopped.
   */
  failedMutations: number;
  failedUploads: number;
  failedMutationDetails: SyncMutation[];
  failedUploadDetails: PendingUpload[];
  conflicts: number;
  conflictDetails: ConflictEntry[];
  error: string | null;
  /**
   * The offline cache is sealed for this session — online works, offline has
   * nothing to fall back on. Surfaced by SyncStatusBar.
   *
   * Set for two reasons now: the cache could not be emptied for the incoming
   * tenant (login), or it could not be opened at all (any time). Both are
   * mirrored automatically from the quarantine flag — see the subscription at
   * the bottom of this file.
   */
  cacheDegraded: boolean;
  /**
   * The signed-in session was restored from this device, not confirmed by the
   * server — `AuthContext.checkAuth` could not reach `/auth/me` and opened from
   * the profile saved here.
   *
   * It lives in this store rather than being read off `AuthContext` because
   * `SyncStatusBar` is the only thing that renders it, and this file's header
   * note is explicit that the bar must stay a leaf: importing the auth context
   * would pull `api/client` into every component that shows a sync count. Same
   * arrangement as `cacheDegraded`, which is mirrored in from `offline/db`.
   *
   * `AuthContext` owns the value and is the only writer; it also exposes it as
   * `sessionFromCache` on the auth context, reading it back from here so the
   * two can never disagree.
   */
  sessionFromCache: boolean;
  /**
   * The last thing that asked the API got no answer: a /health probe that
   * failed or timed out, or a sync whose request never came back. Cleared by
   * the next probe or sync that does get one.
   *
   * NOT the same as `isOnline === false`. The detector waits for two failed
   * probes (three timeouts) before it calls a dead zone, which is right for
   * the offline banner and wrong for "Synced just now": for that minute the
   * bar was telling a hand the app was in touch with the server when the last
   * request to it had just failed. This is the faster, weaker signal, and the
   * only thing it does is stop the bar claiming a fresh sync.
   */
  serverUnreachable: boolean;
  setOnline: (online: boolean) => void;
  setSyncing: (syncing: boolean) => void;
  setLastSyncAt: (ts: string) => void;
  setPendingMutations: (count: number) => void;
  setPendingUploads: (count: number) => void;
  setFailedMutations: (count: number) => void;
  setFailedUploads: (count: number) => void;
  setFailedDetails: (mutations: SyncMutation[], uploads: PendingUpload[]) => void;
  setConflicts: (count: number) => void;
  loadConflictDetails: () => Promise<void>;
  dismissConflict: (id: string) => Promise<void>;
  dismissAllConflicts: () => Promise<void>;
  setError: (error: string | null) => void;
  setCacheDegraded: (degraded: boolean) => void;
  setSessionFromCache: (fromCache: boolean) => void;
  setServerUnreachable: (unreachable: boolean) => void;
}

export const useSyncStore = create<SyncState>((set, get) => ({
  isOnline: navigator.onLine,
  isSyncing: false,
  lastSyncAt: null,
  pendingMutations: 0,
  pendingUploads: 0,
  failedMutations: 0,
  failedUploads: 0,
  failedMutationDetails: [],
  failedUploadDetails: [],
  conflicts: 0,
  conflictDetails: [],
  error: null,
  cacheDegraded: false,
  sessionFromCache: false,
  serverUnreachable: false,
  setOnline: (online) => set({ isOnline: online }),
  setServerUnreachable: (unreachable) => set({ serverUnreachable: unreachable }),
  setSyncing: (syncing) => set({ isSyncing: syncing }),
  setLastSyncAt: (ts) => set({ lastSyncAt: ts }),
  setPendingMutations: (count) => set({ pendingMutations: count }),
  setPendingUploads: (count) => set({ pendingUploads: count }),
  setFailedMutations: (count) => set({ failedMutations: count }),
  setFailedUploads: (count) => set({ failedUploads: count }),
  setFailedDetails: (mutations, uploads) =>
    set({
      failedMutationDetails: mutations,
      failedUploadDetails: uploads,
      failedMutations: mutations.length,
      failedUploads: uploads.length,
    }),
  setConflicts: (count) => {
    set({ conflicts: count });
    if (count > 0) get().loadConflictDetails();
  },
  loadConflictDetails: async () => {
    try {
      const db = await getDB();
      const all = await db.getAll('conflictLog');
      const active = all.filter((c) => !c.dismissed);
      set({ conflictDetails: active, conflicts: active.length });
    } catch {
      // DB not ready yet
    }
  },
  // Dismissal is a user action on a panel that is open in front of them. If
  // the cache went away underneath it, the dismissal still has to close the
  // panel — leaving a rejected promise and a stuck banner would trap the user
  // on a conflict list they cannot clear. The in-memory state is updated
  // either way; only the persisted `dismissed` flag is lost, which at worst
  // means the conflict reappears after a restart.
  dismissConflict: async (id: string) => {
    try {
      const db = await getDB();
      const entry = await db.get('conflictLog', id);
      if (entry) {
        entry.dismissed = true;
        await db.put('conflictLog', entry);
      }
    } catch (err) {
      if (!isCacheUnavailableError(err)) throw err;
      console.warn('[sync-store] Cache unavailable; dismissing conflict in memory only.');
    }
    const { conflictDetails } = get();
    const updated = conflictDetails.filter((c) => c.clientMutationId !== id);
    set({ conflictDetails: updated, conflicts: updated.length });
  },
  dismissAllConflicts: async () => {
    try {
      const db = await getDB();
      const all = await db.getAll('conflictLog');
      for (const entry of all) {
        if (!entry.dismissed) {
          entry.dismissed = true;
          await db.put('conflictLog', entry);
        }
      }
    } catch (err) {
      if (!isCacheUnavailableError(err)) throw err;
      console.warn('[sync-store] Cache unavailable; dismissing conflicts in memory only.');
    }
    set({ conflictDetails: [], conflicts: 0 });
  },
  setError: (error) => set({ error }),
  setCacheDegraded: (degraded) => set({ cacheDegraded: degraded }),
  setSessionFromCache: (fromCache) => set({ sessionFromCache: fromCache }),
}));

/**
 * Mirror the quarantine flag into `cacheDegraded` so the banner appears no
 * matter which path sealed the cache.
 *
 * Before this, only `purgeTenantData` set the flag, so a database that wedged
 * mid-session degraded the app silently — screens quietly stopped having
 * anything to fall back on and nobody told the user. It also means recovery
 * clears the banner: when `getDB`'s delete succeeds and the quarantine lifts,
 * this fires with `false`.
 *
 * Registered at module load rather than from a component, because the login
 * purge can quarantine before any of this app's UI has mounted.
 */
onOfflineCacheQuarantineChange((quarantined) => {
  useSyncStore.getState().setCacheDegraded(quarantined);
});
